import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Filter,
  X,
  ChevronDown,
  SortAsc,
  SortDesc,
  TrendingUp,
  Calendar,
  User,
  Building,
  Target
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Lead } from '@/types/lead';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface SearchAndFilterProps {
  onSearch: (query: string) => void;
  onFilterChange: (filters: FilterState) => void;
  onSortChange: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  searchTerm: string;
  activeFilters: FilterState;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  leads: Lead[];
}

interface FilterState {
  stage: string[];
  status: string[];
  assignedTo: string[];
  sourceId: string[];
  riskFlag: string[];
  minValue?: number;
  maxValue?: number;
  createdAfter?: string;
  createdBefore?: string;
}

export const SearchAndFilter: React.FC<SearchAndFilterProps> = ({
  onSearch,
  onFilterChange,
  onSortChange,
  searchTerm,
  activeFilters,
  sortBy,
  sortOrder,
  leads
}) => {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [localFilters, setLocalFilters] = useState<FilterState>(activeFilters);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search: wait 300ms after last keystroke before notifying parent
  useEffect(() => {
    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      onSearch(localSearchTerm);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [localSearchTerm]); // intentionally omit onSearch to avoid re-triggering on parent re-render

  // Sync local search term if parent resets it (e.g. clear button from outside)
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // Fetch lead sources for filter
  const { data: leadSources } = useQuery({
    queryKey: ['lead-sources'],
    queryFn: () => api.getLeadSources()
  });

  // Get unique values for filter options
  const stages = Array.from(new Set(Array.isArray(leads) ? leads.map(lead => lead.stage) : []));
  const statuses = Array.from(new Set(Array.isArray(leads) ? leads.map(lead => lead.status ?? 'WARM') : []));
  const assignees = Array.from(new Set(Array.isArray(leads) ? leads.map(lead => lead.assignedTo).filter(Boolean) : []));

  const handleFilterChange = (filterType: keyof FilterState, value: any) => {
    const newFilters = { ...localFilters };

    if (filterType === 'stage' || filterType === 'status' || filterType === 'assignedTo' || filterType === 'sourceId' || filterType === 'riskFlag') {
      const currentArray = newFilters[filterType] as string[];
      if (currentArray.includes(value)) {
        newFilters[filterType] = currentArray.filter(item => item !== value) as any;
      } else {
        newFilters[filterType] = [...currentArray, value] as any;
      }
    } else {
      newFilters[filterType] = value as any;
    }

    setLocalFilters(newFilters);
  };

  const applyFilters = () => {
    onFilterChange(localFilters);
    setIsFilterPanelOpen(false);
  };

  const clearAllFilters = () => {
    const clearedFilters: FilterState = {
      stage: [],
      status: [],
      assignedTo: [],
      sourceId: [],
      riskFlag: [],
      minValue: undefined,
      maxValue: undefined,
      createdAfter: undefined,
      createdBefore: undefined
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const getActiveFilterCount = () => {
    return Object.values(activeFilters).reduce((count, value) => {
      if (Array.isArray(value)) {
        return count + value.length;
      }
      return count + (value ? 1 : 0);
    }, 0);
  };

  const sortOptions = [
    { value: 'name', label: 'Name', icon: Building },
    { value: 'value', label: 'Value', icon: TrendingUp },
    { value: 'createdAt', label: 'Created Date', icon: Calendar },
    { value: 'lastActivity', label: 'Last Activity', icon: Calendar },
    { value: 'assignedTo', label: 'Assigned To', icon: User }
  ];

  return (
    <div className="relative">
      {/* Search Bar */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search leads, companies, or deals..."
          value={localSearchTerm}
          onChange={(e) => setLocalSearchTerm(e.target.value)}
          className="pl-10 pr-4 py-2 bg-white/50 backdrop-blur-sm border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
        />
        {localSearchTerm && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
              }
              setLocalSearchTerm('');
              onSearch('');
            }}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Filter and Sort Controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          {/* Filter Button */}
          <Button
            variant="outline"
            onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
            className={`flex items-center space-x-2 ${
              getActiveFilterCount() > 0 ? 'bg-blue-50 border-blue-200 text-blue-700' : ''
            }`}
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
            {getActiveFilterCount() > 0 && (
              <Badge variant="secondary" className="ml-1">
                {getActiveFilterCount()}
              </Badge>
            )}
          </Button>

          {/* Sort Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex items-center space-x-2">
                {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
                <span>Sort</span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              {sortOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => onSortChange(option.value, sortOrder)}
                  className={sortBy === option.value ? 'bg-blue-50' : ''}
                >
                  <option.icon className="h-4 w-4 mr-2" />
                  {option.label}
                  {sortBy === option.value && (
                    <span className="ml-auto text-blue-600">
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Clear Filters */}
          {getActiveFilterCount() > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="text-gray-500 hover:text-gray-700"
            >
              Clear all
            </Button>
          )}
        </div>

        {/* Results Count */}
        <div className="text-sm text-gray-600">
          {leads.length} lead{leads.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Filter Panel */}
      <AnimatePresence>
        {isFilterPanelOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6"
          >
            <Card className="p-6 bg-white/80 backdrop-blur-sm border border-gray-200/50">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Stage Filter */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Pipeline Stage</h3>
                  <div className="space-y-2">
                    {Array.isArray(stages) && stages.map((stage) => (
                      <label key={stage} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={localFilters.stage.includes(stage)}
                          onChange={() => handleFilterChange('stage', stage)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{stage}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Status Filter */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Priority Status</h3>
                  <div className="space-y-2">
                    {Array.isArray(statuses) && statuses.map((status) => (
                      <label key={status} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={localFilters.status.includes(status)}
                          onChange={() => handleFilterChange('status', status)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{status}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Assigned To Filter */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Assigned To</h3>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {Array.isArray(assignees) && assignees.map((assignee) => (
                      <label key={assignee} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={Array.isArray(localFilters.assignedTo) && localFilters.assignedTo.includes(assignee as string)}
                          onChange={() => handleFilterChange('assignedTo', assignee)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{assignee}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Lead Source Filter */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                    <Target className="h-4 w-4 mr-2 text-blue-600" />
                    Lead Source
                  </h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {Array.isArray(leadSources) && leadSources.map((source) => (
                      <label key={source.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={Array.isArray(localFilters.sourceId) && localFilters.sourceId.includes(source.id)}
                          onChange={() => handleFilterChange('sourceId', source.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span
                          className="w-3 h-3 rounded-full ml-2 mr-2"
                          style={{ backgroundColor: source.color || '#6B7280' }}
                        />
                        <span className="text-sm text-gray-700">{source.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Value Range */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Deal Value Range</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Min Value (₹)</label>
                      <Input
                        type="number"
                        value={localFilters.minValue || ''}
                        onChange={(e) => handleFilterChange('minValue', e.target.value ? parseInt(e.target.value) : undefined)}
                        placeholder="0"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Max Value (₹)</label>
                      <Input
                        type="number"
                        value={localFilters.maxValue || ''}
                        onChange={(e) => handleFilterChange('maxValue', e.target.value ? parseInt(e.target.value) : undefined)}
                        placeholder="∞"
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Date Range */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Created Date Range</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">From</label>
                      <Input
                        type="date"
                        value={localFilters.createdAfter || ''}
                        onChange={(e) => handleFilterChange('createdAfter', e.target.value || undefined)}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">To</label>
                      <Input
                        type="date"
                        value={localFilters.createdBefore || ''}
                        onChange={(e) => handleFilterChange('createdBefore', e.target.value || undefined)}
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Filter Actions */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <Button variant="outline" onClick={() => setIsFilterPanelOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={applyFilters}>
                  Apply Filters
                </Button>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active Filter Chips */}
      {getActiveFilterCount() > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {activeFilters.stage.map((stage) => (
            <Badge key={`stage-${stage}`} variant="secondary" className="flex items-center space-x-1">
              <span>Stage: {stage}</span>
              <button
                onClick={() => handleFilterChange('stage', stage)}
                className="ml-1 hover:text-red-600"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {activeFilters.status.map((status) => (
            <Badge key={`status-${status}`} variant="secondary" className="flex items-center space-x-1">
              <span>Status: {status}</span>
              <button
                onClick={() => handleFilterChange('status', status)}
                className="ml-1 hover:text-red-600"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {activeFilters.assignedTo.map((assignee) => (
            <Badge key={`assignee-${assignee}`} variant="secondary" className="flex items-center space-x-1">
              <span>Assigned: {assignee}</span>
              <button
                onClick={() => handleFilterChange('assignedTo', assignee)}
                className="ml-1 hover:text-red-600"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {activeFilters.sourceId?.map((sourceId) => {
            const source = leadSources?.find(s => s.id === sourceId);
            return (
              <Badge key={`source-${sourceId}`} variant="secondary" className="flex items-center space-x-1">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: source?.color || '#6B7280' }}
                />
                <span>Source: {source?.name || sourceId}</span>
                <button
                  onClick={() => handleFilterChange('sourceId', sourceId)}
                  className="ml-1 hover:text-red-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
          {(activeFilters.minValue || activeFilters.maxValue) && (
            <Badge variant="secondary" className="flex items-center space-x-1">
              <span>
                Value: ₹{activeFilters.minValue || 0} - ₹{activeFilters.maxValue || '∞'}
              </span>
              <button
                onClick={() => {
                  handleFilterChange('minValue', undefined);
                  handleFilterChange('maxValue', undefined);
                }}
                className="ml-1 hover:text-red-600"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};